//
// Semaglutide (Wegovy® tablets) PGD — AHT weight management
// Source: AHT_Oral_Wegovy_PGD_v1_1
//
// QUANTITIES ARE PRE-VERIFIED LOOKUPS. DO NOT CALCULATE.
// Quantity basis: 1 × 30-tablet pack per dose step = 30-day supply.

import type { PGD } from "../../types";

export const pgdAhtWtOralSemaglutide: PGD = {
  id: "pgd-aht-wt-oral-semaglutide",
  reference: "AHT-WT-OSEM",
  version: "1.1",
  drugRef: "oral_semaglutide",
  indication: "Weight management — oral semaglutide (Wegovy® tablets)",
  validFrom: "", // TODO: insert valid from date when signed
  expiryDate: "", // TODO: insert expiry date when signed
  frameworkType: "other",
  service: "aht_private",
  commissioner: "Allied Health Training Limited",
  status: "active",

  population: {
    ageRange: { min: 18, max: 84 },
    sex: "any",
    pregnancyAllowed: false,
    breastfeedingAllowed: false,
  },

  consultationRequirements: {
    initialSupply: "face_to_face",
    subsequentSupply: "remote_permitted",
    // Remote supply permitted in line with GPhC February 2025 guidance on
    // pharmacy services at a distance. Pharmacist must be physically present
    // at the registered premises.
  },

  duration: {
    days: 30,
    comment: "30-day supply per dose step (1 × 30-tablet pack). Monthly review.",
  },

  doseBands: [
    {
      id: "osem-step1",
      doseStepId: "osem-1_5mg",
      resolveBy: "single",
      dose: 1.5,
      frequency: "Once daily oral (fasting regimen)",
      comment: "Month 1: initiation dose",
      formulations: [
        {
          formulationId: "oral-sema-1_5mg-tablet",
          instruction:
            "Take one 1.5 mg tablet once daily on an empty stomach after a fast of at least 8 hours. Swallow whole with up to 120 mL water. Wait at least 30 minutes before food, drink or other oral medicines.",
          quantity: { count: 1, label: "1 × Wegovy® 1.5 mg tablets, 30-tablet pack" },
          allowQuantityOverride: false,
        },
      ],
    },
    {
      id: "osem-step2",
      doseStepId: "osem-4mg",
      resolveBy: "single",
      dose: 4,
      frequency: "Once daily oral (fasting regimen)",
      comment: "Month 2: step up if month 1 tolerated",
      formulations: [
        {
          formulationId: "oral-sema-4mg-tablet",
          instruction:
            "Take one 4 mg tablet once daily on an empty stomach after a fast of at least 8 hours. Swallow whole with up to 120 mL water. Wait at least 30 minutes before food, drink or other oral medicines.",
          quantity: { count: 1, label: "1 × Wegovy® 4 mg tablets, 30-tablet pack" },
          allowQuantityOverride: false,
        },
      ],
    },
    {
      id: "osem-step3",
      doseStepId: "osem-9mg",
      resolveBy: "single",
      dose: 9,
      frequency: "Once daily oral (fasting regimen)",
      comment: "Month 3: step up if month 2 tolerated",
      formulations: [
        {
          formulationId: "oral-sema-9mg-tablet",
          instruction:
            "Take one 9 mg tablet once daily on an empty stomach after a fast of at least 8 hours. Swallow whole with up to 120 mL water. Wait at least 30 minutes before food, drink or other oral medicines.",
          quantity: { count: 1, label: "1 × Wegovy® 9 mg tablets, 30-tablet pack" },
          allowQuantityOverride: false,
        },
      ],
    },
    {
      id: "osem-step4",
      doseStepId: "osem-25mg",
      resolveBy: "single",
      dose: 25,
      frequency: "Once daily oral (fasting regimen)",
      comment: "Month 4 onward: maintenance dose — MAXIMUM. Maximum recommended dose is 25 mg once daily.",
      formulations: [
        {
          formulationId: "oral-sema-25mg-tablet",
          instruction:
            "Take one 25 mg tablet once daily on an empty stomach after a fast of at least 8 hours. Swallow whole with up to 120 mL water. Wait at least 30 minutes before food, drink or other oral medicines.",
          quantity: { count: 1, label: "1 × Wegovy® 25 mg tablets, 30-tablet pack" },
          allowQuantityOverride: false,
        },
      ],
    },
  ],

  preSupplyChecks: [
    // INCLUSION — same as injectable semaglutide except face-to-face not
    // required for initial (remote permitted per GPhC 2025 guidance),
    // but initial supply IS face-to-face per PGD — keep face_to_face_confirmed
    {
      id: "inc-age-18-84",
      question: "Is the patient aged 18–84 years?",
      direction: "exclude_on_no",
      autoAnswerFrom: "age_18_to_84",
    },
    {
      id: "inc-bmi-meets-threshold",
      question:
        "Does the patient meet the BMI inclusion threshold? (BMI ≥30; or BMI ≥27 with ≥1 weight-related comorbidity; or BMI ≥27.5 in South Asian, Chinese, other Asian, Middle Eastern, Black African, or African-Caribbean ethnic backgrounds)",
      direction: "exclude_on_no",
      autoAnswerFrom: "sharedBmiInclusionMet",
    },
    {
      id: "inc-monitoring-consent",
      question: "Has the patient consented to monthly monitoring and review?",
      direction: "exclude_on_no",
      autoAnswerFrom: "sharedMonitoringConsent",
    },
    {
      id: "inc-lifestyle-agreement",
      question: "Has the patient agreed to a reduced-calorie diet and increased physical activity?",
      direction: "exclude_on_no",
      autoAnswerFrom: "sharedLifestyleAgreement",
    },
    {
      id: "inc-informed-consent",
      question: "Has the patient provided informed consent?",
      direction: "exclude_on_no",
      autoAnswerFrom: "sharedInformedConsent",
    },
    {
      id: "inc-face-to-face",
      question:
        "Is the patient attending face-to-face at registered premises, or is this a compliant remote consultation with the pharmacist physically present at registered premises?",
      hint: "Initial supply permitted face-to-face or via compliant remote consultation per GPhC February 2025 guidance.",
      direction: "exclude_on_no",
      autoAnswerFrom: "sharedFaceToFace",
    },
    // EXCLUSION CRITERIA
    // IMPORTANT — divergences from injectable semaglutide are annotated.
    {
      id: "ex-bmi-below-27",
      question:
        "Is the patient's BMI below 27 kg/m² (and not already established on oral semaglutide or meeting re-initiation criteria)?",
      autoAnswerFrom: "bmiBelowThreshold",
    },
    {
      id: "ex-bmi-27-30-no-comorbidity",
      question: "Is the patient's BMI 27–30 kg/m² without a weight-related comorbidity?",
      autoAnswerFrom: "bmiInLowRangeNoComorbidity",
    },
    {
      id: "ex-bmi-below-22-5-during-treatment",
      question: "Has the patient's BMI fallen below 22.5 kg/m² during treatment?",
      autoAnswerFrom: "bmiBelowFloor225",
    },
    {
      id: "ex-bmi-monitoring-refusal",
      question: "Is the patient refusing to participate in BMI monitoring?",
      autoAnswerFrom: "sharedMonitoringRefusal",
    },
    {
      id: "ex-anaphylaxis-glp1",
      question:
        "Has the patient had a previous anaphylactic reaction to semaglutide, another GLP-1 receptor agonist, or any incretin mimetic?",
    },
    {
      id: "ex-hypersensitivity-oral-sema",
      // DIVERGENCE: oral tablet excipients differ from injectable
      question:
        "Does the patient have known hypersensitivity to semaglutide or any excipient of the tablet formulation (salcaprozate sodium or magnesium stearate)?",
    },
    {
      id: "ex-t2dm-hba1c-high",
      question: "Does the patient have Type 2 diabetes with HbA1c ≥86 mmol/mol?",
      autoAnswerFrom: "t2dmHba1cHigh",
    },
    {
      id: "ex-concurrent-weight-loss-agents",
      question: "Is the patient currently using other weight loss agents, GLP-1 agonists, or incretin mimetics concurrently?",
      autoAnswerFrom: "sharedConcurrentWeightAgent",
    },
    {
      id: "ex-endocrine-obesity",
      question: "Is the patient's obesity secondary to an endocrine condition under specialist care?",
      autoAnswerFrom: "sharedEndocrineObesity",
    },
    {
      id: "ex-sulphonylureas-insulin",
      // DIVERGENCE DV6: listed as hard exclusion in oral PGD (not just caution)
      question: "Is the patient currently using sulphonylureas or insulin?",
      autoAnswerFrom: "q6SulphonylureaInsulin",
    },
    {
      id: "ex-severe-renal",
      // DIVERGENCE DV4: oral PGD excludes at eGFR <30, not CrCl <15.
      // This check is for eGFR <30 mL/min/1.73m².
      question:
        "Does the patient have renal impairment with eGFR below 30 mL/min/1.73m²? (Oral semaglutide is not recommended in severe renal impairment — verify eGFR, not CrCl, for this check)",
      hint: "The oral semaglutide PGD excludes at eGFR <30 mL/min/1.73m². This is a broader threshold than the CrCl <15 threshold used for injectable semaglutide.",
      autoAnswerFrom: "oralSemaEgfrBelow30",
    },
    {
      id: "ex-severe-hepatic",
      question: "Does the patient have severe hepatic impairment (Child-Pugh C)?",
      // Hepatic impairment is captured at Q7 of the selection pathway, not in
      // the shared assessment — the "shared" prefix is reserved for the latter.
      autoAnswerFrom: "q7SevereHepatic",
    },
    {
      id: "ex-severe-gi",
      question: "Does the patient have severe or unstable gastrointestinal disease, including gastroparesis?",
      autoAnswerFrom: "q14SevereGi",
    },
    {
      id: "ex-pregnancy",
      question:
        "Is the patient pregnant, breastfeeding, or planning pregnancy within the next 2 months? (Semaglutide must be discontinued at least 2 months before a planned pregnancy.)",
      autoAnswerFrom: "sharedPregnancyBreastfeeding",
    },
    {
      id: "ex-t1dm-t3c",
      question: "Does the patient have Type 1 or Type 3c (pancreatogenic) diabetes mellitus?",
      autoAnswerFrom: "q5T1dmT3c",
    },
    {
      id: "ex-retinopathy-any",
      // DIVERGENCE DV5: oral PGD excludes any diabetic retinopathy or
      // macular oedema — not just proliferative or actively treated.
      question: "Does the patient have diabetic retinopathy (any stage, including non-proliferative) or macular oedema?",
      hint: "Oral semaglutide is excluded for any diabetic retinopathy or macular oedema. This is broader than the threshold for injectable semaglutide (proliferative or actively treated only).",
      autoAnswerFrom: "q18RetinopathyAny",
    },
    {
      id: "ex-pancreatitis",
      question: "Does the patient have a personal history of pancreatitis (acute or chronic)?",
      autoAnswerFrom: "q4Pancreatitis",
    },
    {
      id: "ex-thyroid-men2",
      question:
        "Does the patient have a personal or family history of medullary thyroid carcinoma (MTC) or Multiple Endocrine Neoplasia syndrome type 2 (MEN2)?",
      autoAnswerFrom: "q3Thyroid",
    },
  ],

  cautions: [
    { id: "caut-gi-dehydration", text: "GI symptoms (nausea, vomiting, diarrhoea) — dehydration risk; maintain hydration, especially in elderly and those with renal impairment" },
    { id: "caut-gastroparesis", text: "Gastroparesis symptoms (bloating, abdominal pain) — discontinue if develops; semaglutide not recommended where gastroparesis is severe" },
    { id: "caut-pancreatitis", text: "Pancreatitis suspected — stop immediately; do not restart if confirmed; enzyme elevation alone does not confirm pancreatitis" },
    { id: "caut-naion", text: "NAION (non-arteritic anterior ischaemic optic neuropathy) — advise patient to report any sudden loss of vision; refer urgently and discontinue if NAION confirmed" },
    { id: "caut-heart-rate", text: "Monitor resting heart rate; report mood or behavioural changes" },
    { id: "caut-not-insulin-substitute", text: "Not a substitute for insulin; must not be used in type 1 diabetes or diabetic ketoacidosis" },
    { id: "caut-aspiration", text: "Aspiration risk — advise of delayed gastric emptying before general anaesthesia or deep sedation; patient must inform anaesthetist/surgical team" },
    { id: "caut-renal-gi", text: "Renal function may deteriorate with prolonged GI effects — monitor" },
    { id: "caut-oral-contraceptives", text: "Oral contraceptives: semaglutide does not reduce contraceptive exposure, but vomiting within 3 hours or severe diarrhoea over 24 hours can — advise missed-pill guidance and barrier cover until resolved" },
    { id: "caut-hrt-progestogen", text: "HRT (oral progestogen): semaglutide delays gastric emptying; keep oral progestogen outside the 30-minute fasting window; advise patient to consult HRT prescriber before starting or at dose increase" },
  ],

  counselling: [
    "Take on an empty stomach after a fast of at least 8 hours — on waking is recommended",
    "Swallow tablet whole with up to 120 mL (half a glass) of water — do not split, crush or chew",
    "Wait at least 30 minutes before any food, drink or other oral medicine — less than 30 minutes reduces absorption",
    "One tablet daily; never take two tablets to make up for a missed dose or achieve a higher effect",
    "Missed dose: skip it and take the next dose the following day",
    "No cold chain — store in original blister at room temperature, protected from moisture and light",
    "Check blister integrity and expiry before supply; record batch number and expiry date",
    "Provide PIL; ensure patient understands the fasting and timing requirements at initiation and each dose change",
    "Advise patient to inform GP on initiation and at each dose change; clinician must also notify GP",
    "Pancreatitis warning: severe persistent abdominal pain radiating to the back with nausea and vomiting — seek immediate medical attention",
    "NAION: report any sudden vision loss, including partial loss",
    "Encourage adequate fluid intake to manage GI side effects",
    "If interrupted: re-initiate at a lower dose and re-titrate according to tolerability",
    "Documentation retained 8 years",
  ],

  efficacyReview: {
    timepoint: "6 months at maximum tolerated dose",
    threshold: "≥5% weight loss",
    actionIfNotMet: "Discontinue — stop treatment, refer to GP, offer lifestyle support",
  },

  monitoring: {
    frequency: "monthly",
    parameters: [
      "Weight",
      "BMI",
      "Waist circumference (strongly recommended)",
      "Side effects",
      "HbA1c if Type 2 diabetes",
      "Dose titration every 4 weeks based on response and tolerance",
    ],
    discontinueIf: ["Less than 5% weight loss after 6 months at maximum tolerated dose"],
  },

  reviewSchedule: {
    supplyIntervalDays: 30,
    drugSpecificReviewDays: 180,
    drugSpecificReviewLabel: "6-month efficacy review",
    efficacyThresholdPercent: 5,
    stopRuleDescription: "Discontinue if <5% weight loss after 6 months at maximum tolerated dose",
    sectionBReviewDays: 180,
    bmiFloorKgM2: 22.5,
    titrationSchedule: undefined,
    maxEpisodesBeforeReview: null,
    requiresBpPulseAtEscalation: false,
    requiresBrandAndBatchAtEverySupply: false,
  },

  adverseEventGuidance: [
    "Anaphylaxis/angioedema: STOP immediately — refer urgently; discontinue permanently",
    "Pancreatitis: STOP immediately — do not restart if confirmed",
    "NAION: discontinue and refer urgently for ophthalmological assessment",
    "Gastroparesis: discontinue and review",
    "Dehydration with GI effects: review; advise fluid intake",
    "Report locally, notify GP, MHRA Yellow Card (yellowcard.mhra.gov.uk)",
    "Black Triangle medicine — ALL suspected ADRs must be reported",
  ],
};
